import React, { useState } from "react";
import { CardComp } from "../../../../components";
import { useGetSingleEventWithHistoryQuery } from "../../../../store/admin/eventSlice";
import CommunicationHistory from "./CommunicationHistory";

export default function EventDetailsModal({ eventData, onClose }) {
  const [activeTab, setActiveTab] = useState("details");
  
  // Fetch event with history when communication history tab is active
  const {
    data: eventWithHistory,
    isLoading: isLoadingHistory,
    error: historyError,
  } = useGetSingleEventWithHistoryQuery(eventData?.id, {
    skip: activeTab !== "history" || !eventData?.id,
  });

  // Use event with history if available, otherwise use eventData
  const currentEventData = eventWithHistory?.event || eventData;
  const communicationHistory = eventWithHistory?.communication_history || {};
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "Not specified";
    return timeString;
  };

  const getEventStatus = () => {
    if (!currentEventData?.start_date || !currentEventData?.end_date) return "Unknown";

    const now = new Date();
    const startDate = new Date(currentEventData.start_date);
    const endDate = new Date(currentEventData.end_date);

    if (now < startDate) {
      return "Upcoming";
    } else if (now >= startDate && now <= endDate) {
      return "Active";
    } else {
      return "Past";
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Upcoming: {
        class: "bg-warning",
        icon: "fas fa-clock",
        text: "Upcoming Event",
      },
      Active: {
        class: "bg-success",
        icon: "fas fa-check-circle",
        text: "Active Event",
      },
      Past: {
        class: "bg-secondary",
        icon: "fas fa-history",
        text: "Past Event",
      },
      Unknown: {
        class: "bg-info",
        icon: "fas fa-question-circle",
        text: "Unknown Status",
      },
    };

    const config = statusConfig[status] || statusConfig["Unknown"];

    return (
      <span className={`badge ${config.class} fs-6 px-3 py-2`}>
        <i className={`${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  // Tabs configuration
  const tabs = [
    { name: "Event Details", key: "details" },
    { name: "Communication History", key: "history" },
  ];

  // Render event details content
  const renderEventDetails = () => (
    <CardComp cardClassName="border-0 shadow-sm" cardBodyClassName="p-4">
        <div className="row g-4">
          {/* Event Name */}
          <div className="col-12">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-calendar-alt text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Event Name</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {currentEventData?.name || "Not specified"}
            </p>
          </div>

          {/* Event Description */}
          <div className="col-12">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-align-left text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Description</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {currentEventData?.description || "Not specified"}
            </p>
          </div>

          {/* Date and Time Information */}
          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-calendar text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Start Date</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {formatDate(currentEventData?.start_date)}
            </p>
          </div>

          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-calendar text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">End Date</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {formatDate(currentEventData?.end_date)}
            </p>
          </div>

          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-clock text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Start Time</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {formatTime(currentEventData?.start_time)}
            </p>
          </div>

          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-hourglass-half text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Duration</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {currentEventData?.duration
                ? `${currentEventData.duration} hours`
                : "Not specified"}
            </p>
          </div>

          {/* Venue and Attendees */}
          <div className="col-12">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-map-marker-alt text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Venue Address</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {currentEventData?.address || "Not specified"}
            </p>
          </div>

          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-users text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Attendees</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {currentEventData?.attendees || "Not specified"}
            </p>
          </div>

          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2">
              <i className="fas fa-bell text-primary1 me-2"></i>
              <h5 className="mb-0 text-dark fw-bold">Reminder Count</h5>
            </div>
            <p className="text-muted mb-0 ps-4">
              {currentEventData?.reminder_count || "Not specified"}
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="d-flex justify-content-center">
              {getStatusBadge(getEventStatus())}
            </div>
          </div>
        </div>
      </CardComp>
  );

  // Render communication history content
  const renderCommunicationHistory = () => (
    <CommunicationHistory
      communicationHistory={communicationHistory}
      isLoading={isLoadingHistory}
      eventId={eventData?.id}
    />
  );

  return (
    <div className="py-3">
      <div className="text-center mb-4">
        <h3 className="text-primary1 fw-bold mb-2">Event Details</h3>
        <div
          className="border-bottom border-2 border-primary1 mx-auto"
          style={{ width: "100px" }}
        ></div>
      </div>

      {/* Tabs */}
      <div className="mb-3">
        <div className="d-flex gap-4 border-bottom">
          {tabs.map((tab) => (
            <div
              key={tab.key}
              className={`pb-2 pointer fw-bold transition-all ${
                activeTab === tab.key
                  ? "text-primary1 border-bottom border-primary1 border-3"
                  : "text-blacklight hover:text-black1"
              }`}
              onClick={() => setActiveTab(tab.key)}
              style={{ fontSize: "16px", cursor: "pointer" }}
            >
              {tab.name}
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-3">
        {activeTab === "details" && renderEventDetails()}
        {activeTab === "history" && renderCommunicationHistory()}
      </div>
    </div>
  );
}
